import imageFemale from 'assets/img/icons/avatar-female.svg';
import imageMale from 'assets/img/icons/avatar-male.svg';
import moment from 'moment-timezone';
import { Physician, PhysicianPhysicianTypeEnum } from 'generated/api';

class PhysicianUtils {
  PHYSICIAN_SKILLS: Record<string, string>;

  constructor() {
    this.PHYSICIAN_SKILLS = {
      iud: 'IUD',
      biopsy: 'Endometrial Biopsy',
      menopause: 'Menopause',
      mensHealth: "Men's Health",
      functional: 'Functional Medicine',
      executiveHealth: 'Executive Health',
    };
  }

  getName(physician: Physician | undefined, errorPlaceholder?: string): string | undefined {
    let error: string | undefined = undefined;

    if (!physician) return error;

    let name: string | undefined = '';

    if (physician.first_name !== undefined) name = physician.first_name;

    if (physician.last_name !== undefined) name += ' ' + physician.last_name;

    name = name!.trim();

    if (name === '') name = error;

    if (name === error && errorPlaceholder !== undefined) name = errorPlaceholder;

    return name;
  }

  getFirstName(physician: Physician | undefined, errorPlaceholder?: string): string | undefined {
    let error: string | undefined = undefined;

    if (!physician) return error;

    let name: string | undefined = '';

    if (physician.first_name !== undefined) name = physician.first_name;

    name = name!.trim();

    if (name === '') name = error;

    if (name === error && errorPlaceholder !== undefined) name = errorPlaceholder;

    return name;
  }

  getInitials(physician: Physician | undefined): string {
    let name = (this.getName(physician) || '').trim();
    let split = name.split(' ');
    split = split.map((word) => word.charAt(0));
    return split.join('');
  }

  getSpecialty(physician: Physician | undefined): string {
    let specialty = '';

    if (physician && physician.specialty) specialty = physician.specialty;

    return specialty;
  }

  getNameWithSpecialty(
    physician: Physician | undefined,
    placeholder: string = 'Unassigned Practitioner',
  ): string {
    if (!physician) return placeholder;
    return (
      this.getName(physician) +
      (this.getSpecialty(physician) ? ` (${this.getSpecialty(physician)})` : '')
    );
  }

  getPhotoUrls(physician: Physician | undefined): [string | undefined, string] {
    return [physician?.photo, this.getGender(physician) === 'Female' ? imageFemale : imageMale];
  }

  getGender(physician: Physician | undefined): string {
    let gender = 'Unknown Gender';

    if (physician === undefined) return gender;

    if (physician.gender !== undefined) gender = physician.gender;

    return gender;
  }

  isNursePractitioner(physician: Physician): boolean {
    return (
      !physician.intern_of &&
      (!physician.physician_type ||
        physician.physician_type === PhysicianPhysicianTypeEnum.NursePractitioner)
    );
  }

  /**
   * Mirrors the backend's physicianUtils.isTemporarilyUnavailable: while true,
   * the practitioner's patients can book primary-only concerns with anyone.
   * The end date is evaluated at read time (nothing clears the flag), and
   * unset or 0 means no end date.
   */
  isTemporarilyUnavailable(physician: Physician | undefined): boolean {
    if (!physician?.temporarily_unavailable) return false;
    const until = physician.temporarily_unavailable_until;
    return !until || until > moment().unix();
  }
}

export default new PhysicianUtils();
